/*━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━*/

/**
 * setGsap.js
 */

/*━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━*/

/**
 * __00: 初期設定
 **━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━*/
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

/**
 * 初期値
 */
let mm = gsap.matchMedia();
let actionOptions = { duration: 1, ease: 'power4.easeOut', overwrite: true, }
let triggerOptions = { start: `top 65%`, end: `bottom 65%`, once: true }
let pinOptions = { scrub: 1, pin: true, anticipatePin: 1, invalidateOnRefresh: true, }

let mediaQuery__pc = "(min-width: 835px)";
let mediaQuery__sp = "(max-width: 834px)";

/**
 * 処理
 **━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━*/

/**
 * __00:アニメーション
 * @name setGSAPInit
 * @param {string} getTrigger - スクロールトリガーとなる要素
 * @param {string} type - アニメーションの種類
 * @description - 指定クラスが特定の位置に来たらアニメーション実行処理
 */
function setGSAPInit(getTrigger, type = '') {

  // フェードイン
  const fadeIn = () => {
    let fadeIn__item = getTrigger.querySelectorAll('.--js_fadeIn__item');
    if (fadeIn__item.length <= 0) return;
    gsap.set(fadeIn__item, { opacity: 0.00001, });
    ScrollTrigger.batch(getTrigger, {
      onEnter: () => gsap.to([fadeIn__item], { ...actionOptions, opacity: 1, stagger: 0.1, }),
      ...triggerOptions,
      // markers: true,
    });
  }

  // フェードアップ
  const fadeUp = () => {
    let fadeUp__item = getTrigger.querySelectorAll('.--js_fadeUp__item');
    if (fadeUp__item.length <= 0) return;
    gsap.set(fadeUp__item, { opacity: 0.00001, y: "25%" });
    ScrollTrigger.batch(getTrigger, {
      onEnter: () => gsap.to([fadeUp__item], { ...actionOptions, opacity: 1, y: 0, stagger: 0.1, }),
      ...triggerOptions,
      // markers: true,
    });
  }

  //animation state
  const animationState = () => {
    let animationState = getTrigger.querySelectorAll('.--js_animationState__item');
    if (animationState.length <= 0) return;
    ScrollTrigger.batch(getTrigger, {
      onEnter: () => gsap.to([animationState], { animationPlayState: "running" }),
      onEnterBack: () => gsap.to([animationState], { animationPlayState: "paused" }),
      start: `top bottom`, end: `top bottom`, once: false
    });
  }

  //猫耳
  const catEar = () => {
    let catEar = getTrigger.querySelectorAll('.cat-ear');
    if (catEar.length <= 0) return;
    ScrollTrigger.create({
      trigger: getTrigger,
      onEnter: () => catEar.forEach((el) => el.classList.add('--show')),
      ...triggerOptions,
    });
  }

  type == 'fadeIn' && fadeIn();
  type == 'fadeUp' && fadeUp();
  type == 'animationState' && animationState();
  type == 'catEar' && catEar();
}

/**
 * @name setGsap__scrollSlide_X
 * @param {string} getTrigger - スクロールトリガーとなる要素
 * @param {string} target__el - スクロール対象となる要素
 * @description - スクロール量に応じて横スクロール
 */
const setGsap__scrollSlide_X = (getTrigger) => {
  let setWidth = getTrigger.clientWidth;
  let slideTaregetEl = getTrigger.querySelectorAll(".--js_slideTarget-el");
  if (slideTaregetEl.length <= 0) return;
  slideTaregetEl.forEach((target__el) => {
    let scrollSlideTriggerTimeline = gsap.timeline({ ease: "none", scrollTrigger: { trigger: getTrigger, start: () => `top top`, end: () => `+=${target__el.clientWidth - setWidth}`, ...pinOptions } });
    scrollSlideTriggerTimeline.to(target__el, { x: () => `-${target__el.clientWidth - setWidth}`, overwrite: true });
  })
}

/**
 * @name setGsap__member_pc
 * @param {string} getTrigger - スクロールトリガーとなる要素
 * @description - TOPページのプロフィールアニメーション
 */
const setGsap__member_pc = (getTrigger) => {
  let bgRadius = getTrigger.querySelector(".bg-radius");
  let member__el = getTrigger.querySelectorAll(".member__list-item");
  if (member__el.length <= 0) return;
  gsap.set(bgRadius, { scaleY: 0.4, });
  gsap.set(member__el, { xPercent: 100, opacity: 0.00001, });
  ScrollTrigger.batch(getTrigger, {
    onEnter: () => {
      gsap.to(bgRadius, { ...actionOptions, scaleY: 1, });
      gsap.to([member__el], { ...actionOptions, xPercent: 0, opacity: 1, stagger: 0.2, })
    },
    ...triggerOptions,
    // markers: true,
  });
}

/**
 * @name setGsap__member_sp
 * @param {string} getTrigger - スクロールトリガーとなる要素
 * @description - TOPページのプロフィールアニメーション
 */
const setGsap__member_sp = (getTrigger) => {
  let member__el = getTrigger.querySelectorAll(".member__list-item");
  if (member__el.length <= 0) return;
  gsap.set(member__el, { opacity: 0.00001, });
  ScrollTrigger.batch(getTrigger, {
    onEnter: () => {
      gsap.to([member__el], { ...actionOptions, opacity: 1, stagger: 0.2, })
    },
    ...triggerOptions,
    // markers: true,
  });
}


/**
 * __10:処理
 * @name setGsap
 * @param {string} getTrigger - スクロールトリガーとなる要素
 */
function setGsap() {
  let setTrigger = document.querySelectorAll('.--js_setTrigger');
  setTrigger.forEach((getTrigger) => {
    setGSAPInit(getTrigger, 'fadeIn');
    setGSAPInit(getTrigger, 'fadeUp');
    setGSAPInit(getTrigger, 'animationState');
    setGSAPInit(getTrigger, 'catEar');
    mm.add(mediaQuery__pc, () => {
      setGsap__member_pc(getTrigger)
      setGsap__scrollSlide_X(getTrigger)
    })
      .add(mediaQuery__sp, () => {
        setGsap__member_sp(getTrigger)
      })
  });
}

/**
 * 書き出し
 **━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━*/
export {
  setGsap
};